import { MutationHookOptions } from '@apollo/client';
import { useIonViewDidEnter, useIonViewDidLeave } from '@ionic/react';
import { useCallback, useEffect, useState } from 'react';
import { usePrevious } from '../../util/hooks';
import {
  MarkEventNotificationsReadMutation,
  MarkEventNotificationsReadMutationVariables,
  SubscribeToEventNotificationUnreadCountDocument,
  SubscribeToEventNotificationUnreadCountSubscriptionResult,
  useMarkEventNotificationsReadMutation,
} from '../graphql/generated';
import { useApolloSharedSubscription } from '../subscription/apollo-subscription.manager';

const useUnreadNotifications = (): number | undefined => {
  const { data: unreadNotificationsData } =
    useApolloSharedSubscription<SubscribeToEventNotificationUnreadCountSubscriptionResult>(
      {
        query: SubscribeToEventNotificationUnreadCountDocument,
      }
    );

  const [unreadNotifications, setUnreadNotifications] = useState<
    number | undefined
  >(undefined);

  useEffect(() => {
    if (
      unreadNotificationsData?.subscribeToEventNotificationUnreadCount !==
      undefined
    ) {
      setUnreadNotifications(
        unreadNotificationsData.subscribeToEventNotificationUnreadCount
      );
    }
  }, [unreadNotificationsData]);

  return unreadNotifications;
};

const useOnNotification = (
  cb: (unread: number | undefined) => void
): number | undefined => {
  const unread = useUnreadNotifications();
  const previousUnread = usePrevious(unread);

  if (unread !== previousUnread) {
    cb(unread);
  }
  return unread;
};

type AutoMarkNotificationReadBySource = (
  baseOptions?: MutationHookOptions<
    MarkEventNotificationsReadMutation,
    MarkEventNotificationsReadMutationVariables
  >
) => Promise<void>;

const useAutoMarkNotificationReadBySource = (
  baseOptions?: MutationHookOptions<
    MarkEventNotificationsReadMutation,
    MarkEventNotificationsReadMutationVariables
  >
): AutoMarkNotificationReadBySource => {
  const [isViewing, setIsViewing] = useState<boolean>(false);

  const [markEventNotificationsReadMutation] =
    useMarkEventNotificationsReadMutation(baseOptions);

  useIonViewDidEnter(() => {
    markEventNotificationsReadMutation();
    setIsViewing(true);
  });

  useIonViewDidLeave(() => {
    setIsViewing(false);
  });

  const callback = useCallback(
    async (
      baseOptions?: MutationHookOptions<
        MarkEventNotificationsReadMutation,
        MarkEventNotificationsReadMutationVariables
      >
    ): Promise<void> => {
      if (isViewing) {
        markEventNotificationsReadMutation(baseOptions);
      }
    },
    [isViewing, markEventNotificationsReadMutation]
  );

  return callback;
};

export {
  useUnreadNotifications,
  useOnNotification,
  useAutoMarkNotificationReadBySource,
};
